import { Action } from 'redux';

import { EcsProjectTeamName, Experiment, FeatureRollout, Feed, YammerState } from '../state/types';

export type MimeBinary = 'application/octet-stream';
export const MimeBinary = 'application/octet-stream';
export type MimeForm = 'application/x-www-form-urlencoded';
export const MimeForm = 'application/x-www-form-urlencoded';
export type MimeJson = 'application/json';
export const MimeJson = 'application/json';
export type MimeText = 'text/plain';
export const MimeText = 'text/plain';
export type MimeFormData = 'multipart/form-data';
export const MimeFormData = 'multipart/form-data';
export type MimeImage = 'image/';
export const MimeImage = 'image/';
export type MimeVideo = 'video/';
export const MimeVideo = 'video/';
export type MimePdf = 'application/pdf';
export const MimePdf = 'application/pdf';
export type MimeAny = '*/*';
export const MimeAny = '*/*';

export type ApiMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiQuery {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly [key: string]: any;
}

export type ApiHeaders = Record<string, string>;

export enum ApiRequestType {
  Ecs = 'Ecs',
  Graphql = 'Graphql',
  LegacyYammer = 'LegacyYammer',
  Ocps = 'Ocps',
  Ocv = 'Ocv',
  SharePoint = 'SharePoint',
  Substrate = 'Substrate',
  ThirdParty = 'ThirdParty',
}

export enum RequestInitiator {
  Realtime = 'realtime',
  RealtimeRetry = 'realtime_retry',
  RealtimeNotification = 'realtime_notification',
}

export interface ApiRequest {
  readonly path: string;
  readonly host?: string;
  readonly query?: ApiQuery;
  readonly method?: ApiMethod;
  readonly mode?: RequestMode;
  // eslint-disable-next-line @typescript-eslint/ban-types
  readonly body?: object | string;
  readonly contentType?: MimeForm | MimeJson | MimeText | MimeFormData;
  readonly responseType?: MimeBinary | MimeForm | MimeJson | MimeText | MimeAny;
  readonly headers?: ApiHeaders;
  readonly apiName?: string;
  readonly requestType: ApiRequestType;
  readonly credentials?: RequestCredentials;
  readonly initiator?: RequestInitiator;
}

export type ApiMiddleware = (request: ManagedApiRequest, state: YammerState) => Promise<ManagedApiRequest>;
export type ExecuteRequest<TResponse> = (request: ApiRequest) => Promise<ApiResponse<TResponse>>;

export interface ApiResponse<TResponse> {
  readonly body: TResponse;
  readonly headers: Headers;
}

export interface CachedApiActionOptions {
  readonly request: ManagedApiRequest;
  readonly caching?: ManagedApiCachingWithKey;
}

export interface ManagedApiRequest extends ApiRequest {
  readonly resource?: string;
  readonly scopes?: string[];
}

export enum ManagedApiCachingStrategy {
  FetchAndWriteCache = 'FetchAndWriteCache',
  FetchOnly = 'FetchOnly',
  ReadCacheOrFetch = 'ReadCacheOrFetch',
  ReadCacheAndRevalidate = 'ReadCacheAndRevalidate',
}

export type CacheHitCallback = () => void;
export type CacheUpdateCallback = () => void;

export interface ManagedApiCaching {
  readonly strategy: ManagedApiCachingStrategy;
  readonly ttlInSeconds?: number;
  readonly cacheHitCallback?: CacheHitCallback;
  readonly cacheUpdateCallback?: CacheUpdateCallback;
}

export interface ManagedApiCachingWithKey extends ManagedApiCaching {
  readonly key: string;
}

/**
 * Actions that ought to be dispatched at the correct moments in time applicable for the API request.
 * - When the request starts, pending will be dispatched
 * - If the request succeeds, fulfilled will be dispatched
 * - If the request fails, rejected will be dispatched
 * - If any logic in the api action throws, rejected will be dispatched
 */
export interface ManagedApiLifecycle<TResponse> {
  pending(): Action;
  fulfilled(response: TResponse): Action;
  rejected(error: unknown): Action;
}

export enum ManagedApiActionName {
  InitializeEcsFeatureFlags = 'initializeEcsFeatureFlags',
  LoadFeed = 'loadFeed',
  LoadOlderThreads = 'loadOlderThreads',
  LoadNewerThreads = 'loadNewerThreads',
  LoadThread = 'loadThread',
  LoadOlderReplies = 'loadOlderReplies',
  LoadNewerReplies = 'loadNewerReplies',
  LoadMessage = 'loadMessage',
  LoadNavigationGroups = 'loadNavigationGroups',
  MarkSeenAndLoadNotifications = 'markSeenAndLoadNotifications',
  PublishMessage = 'publishMessage',
  SearchThreads = 'searchThreads',
  SearchGroups = 'searchGroups',
  SearchUsers = 'searchUsers',
  SearchFiles = 'searchFiles',
  SearchInbox = 'searchInbox',
  SearchTopics = 'searchTopics',
  SearchNetworkQuestion = 'searchNetworkQuestion',
  SearchPublisherNetworkQuestion = 'searchPublisherNetworkQuestion',
}

type ManagedApiActionType = Feed['subtype'];

export interface ManagedApiTelemetry {
  readonly actionName: ManagedApiActionName;
  readonly actionType?: ManagedApiActionType;
  readonly cacheExists?: boolean;
  readonly idempotencyToken?: string;
}

export interface ManagedApiActionOptions<TResponse> {
  readonly request: ManagedApiRequest;
  readonly lifecycle: ManagedApiLifecycle<TResponse>;
  readonly telemetry?: ManagedApiTelemetry;
  readonly caching?: ManagedApiCachingWithKey;
}

export interface GraphqlApiRequestQuery {
  readonly contents: string;
  readonly sha256Hash?: string;
}

export interface GraphqlApiTreatmentRecord {
  readonly project: EcsProjectTeamName;
  readonly key: Experiment | FeatureRollout;
  readonly value: string;
}
export interface GraphqlApiRequestBody<T = undefined> {
  readonly operationName: string;
  readonly query: GraphqlApiRequestQuery;
  readonly variables?: T;
  readonly extensions?: {
    readonly yammerTreatments?: {
      readonly version: '1';
      readonly treatments: GraphqlApiTreatmentRecord[];
    };
  };
}

export interface GraphqlApiRequest<T = undefined> extends ManagedApiRequest {
  readonly path: '/graphql';
  readonly method: 'POST';
  readonly body: GraphqlApiRequestBody<T>;
  readonly apiName: string;
  readonly requestType: ApiRequestType.Graphql;
}

export enum GraphqlErrorReason {
  AddingForeignUserBlocked = 'ADDING_FOREIGN_USER_BLOCKED_IN_POSTERS_NETWORK_ERROR',
  AccessForbidden = 'ACCESS_FORBIDDEN',
  BodyBlank = 'BODY_BLANK',
  BodyTooLong = 'BODY_TOO_LONG',
  CallerNotAdmin = 'CALLER_NOT_ADMIN',
  CallerNotOrganizer = 'CALLER_NOT_ORGANIZER',
  CannotCancelLiveEvent = 'CANNOT_CANCEL_LIVE_EVENT',
  CannotInviteThisRecipientToGroup = 'CANNOT_INVITE_THIS_RECIPIENT_TO_GROUP',
  CannotRevokeLastAdmin = 'CANNOT_REVOKE_LAST_ADMIN',
  CannotUpdateFinishedEvent = 'CANNOT_UPDATE_FINISHED_EVENT',
  CannotUpdateStartTimeInLiveState = 'CANNOT_UPDATE_START_TIME_IN_LIVE_STATE',
  ConflictingGroup = 'CONFLICTING_GROUP',
  ConflictingNetwork = 'CONFLICTING_NETWORK',
  ConflictingPrivacyLevels = 'CONFLICTING_PRIVACY_LEVELS',
  ConflictingReplyTo = 'CONFLICTING_REPLY_TO',
  ConflictingThread = 'CONFLICTING_THREAD',
  ConsumerEmailNotSupported = 'CONSUMER_EMAIL_NOT_SUPPORTED',
  CrossGeoGuestNotAllowed = 'CROSS_GEO_FOREIGN_USER_NOT_SUPPORTED',
  DirectMessageFileUploadForbidden = 'DIRECT_MESSAGE_FILE_UPLOAD_FORBIDDEN',
  DisplayNameAlreadyExists = 'DISPLAY_NAME_ALREADY_EXISTS',
  EndTimeLessThanStartTime = 'END_TIME_LESS_THAN_START_TIME',
  EventDescriptionLongerThan4000 = 'EVENT_DESCRIPTION_LONGER_THAN_4000',
  EventDurationMoreThan4Hours = 'EVENT_DURATION_MORE_THAN_4_HOURS',
  EventTitleLongerThan1000 = 'EVENT_TITLE_LONGER_THAN_1000',
  ExternalNotAllowed = 'EXTERNAL_NOT_ALLOWED',
  ExternalForbiddenOnCurrentUserNetwork = 'EXTERNAL_FORBIDDEN_ON_CURRENT_USER_NETWORK',
  ExternalForbiddenOnForeignUserNetwork = 'EXTERNAL_FORBIDDEN_ON_FOREIGN_USER_NETWORK',
  FavoriteLimitReached = 'FAVORITE_LIMIT_REACHED',
  FileCountExceeded = 'FILE_COUNT_EXCEEDED',
  FileStorageQuotaLimitReached = 'FILE_STORAGE_QUOTA_LIMIT_REACHED',
  FinalAdminWarning = 'FINAL_ADMIN_WARNING',
  GreaterThanHundredPollOptions = 'GREATER_THAN_HUNDRED_POLL_OPTIONS',
  GroupCreationForbidden = 'GROUP_CREATION_FORBIDDEN',
  GroupDeleted = 'GROUP_DELETED',
  GroupDoesNotExist = 'GROUP_DOES_NOT_EXIST',
  GroupNotPresent = 'GROUP_NOT_PRESENT',
  IncorrectMessageType = 'INCORRECT_MESSAGE_TYPE',
  InsufficientPrivilegesToAddGuest = 'INSUFFICIENT_PRIVILEGE_TO_ADD_GUEST',
  InvalidBodyFormat = 'INVALID_BODY_FORMAT',
  InvalidContentState = 'INVALID_CONTENT_STATE',
  InvalidDescription = 'INVALID_DESCRIPTION',
  InvalidDisplayName = 'INVALID_DISPLAY_NAME',
  InvalidEmailAddress = 'INVALID_EMAIL_ADDRESS',
  InvalidIdFormat = 'INVALID_ID_FORMAT',
  InvalidInvitedUsers = 'INVALID_INVITED_USERS',
  InvalidPraise = 'INVALID_PRAISE',
  InvalidReplyReferenced = 'INVALID_REPLY_REFERENCED',
  InvalidStateTransition = 'INVALID_STATE_TRANSITION',
  InvalidTopic = 'INVALID_TOPIC',
  InvalidUrlSchemeInBody = 'INVALID_URL_SCHEME_IN_BODY',
  InvitedUsersOverLimit = 'INVITED_USERS_OVER_LIMIT',
  JsonInvalid = 'JSON_INVALID',
  LimitedCapabilities = 'LIMITED_CAPABILITIES',
  MessageDeleted = 'MESSAGE_DELETED',
  MissingGroup = 'MISSING_GROUP',
  MissingTenantId = 'MISSING_TENANT_ID',
  MissingUserLocale = 'MISSING_USER_LOCALE',
  NetworkBlank = 'NETWORK_BLANK',
  NetworkNotPresent = 'NETWORK_NOT_PRESENT',
  NoAccessToGroup = 'NO_ACCESS_TO_GROUP',
  NoPermissionToAnnounce = 'NO_PERMISSION_TO_ANNOUNCE',
  NoPermissionToCreateQuestion = 'NO_PERMISSION_TO_CREATE_QUESTION',
  NotAnAdmin = 'NOT_AN_ADMIN',
  NotFound = 'NOT_FOUND',
  NoYammerLicense = 'NO_YAMMER_LICENSE',
  None = 'NONE',
  NotEnoughPollOptions = 'NOT_ENOUGH_POLL_OPTIONS',
  OneDriveNotProvisioned = 'ONE_DRIVE_NOT_PROVISIONED',
  OneDriveStorageQuotaLimitReached = 'ONE_DRIVE_STORAGE_QUOTA_LIMIT_REACHED',
  PendingUser = 'PENDING_USER',
  PinLimitReached = 'PIN_LIMIT_REACHED',
  PollOptionAboveCharacterLimit = 'POLL_OPTION_ABOVE_CHAR_LIMIT',
  PollOptionsNotAnArray = 'POLL_OPTIONS_NOT_AN_ARRAY',
  PresenterIsAlsoProducer = 'PRESENTER_IS_ALSO_PRODUCER',
  PresenterOutsideOfPrivateGroup = 'PRESENTER_OUTSIDE_OF_PRIVATE_GROUP',
  PrincipalNotFound = 'PRINCIPAL_NOT_FOUND',
  ProducerOutsideOfPrivateGroup = 'PRODUCER_OUTSIDE_OF_PRIVATE_GROUP',
  ProducerRequired = 'PRODUCER_REQUIRED',
  PublishMessagePartialError = 'PUBLISH_MESSAGE_PARTIAL_ERROR',
  RecipientNotInvitable = 'RECIPIENT_NOT_INVITABLE',
  RecipientOutsideOfNetwork = 'RECIPIENT_OUTSIDE_OF_NETWORK',
  ReplyToFlexternalThreadBlockedInPosterNetwork = 'REPLY_TO_FLEXTERNAL_THREAD_BLOCKED_IN_POSTERS_NETWORK_ERROR',
  ReplyToFlexternalThreadBlockedInReceiverNetwork = 'REPLY_TO_FLEXTERNAL_THREAD_BLOCKED_IN_RECEIVERS_NETWORK_ERROR',
  RestrictedPostingInEvent = 'RESTRICTED_POSTING_IN_EVENT',
  RestrictedPostingInGroup = 'RESTRICTED_POSTING_IN_GROUP',
  SenderBlank = 'SENDER_BLANK',
  SenderCreatorMismatch = 'SENDER_CREATOR_MISMATCH',
  SenderNotActive = 'SENDER_NOT_ACTIVE',
  SenderNotParticipant = 'SENDER_NOT_PARTICIPANT',
  SenderNotPresent = 'SENDER_NOT_PRESENT',
  SpamDetected = 'SPAM_DETECTED',
  StartTimeInPast = 'START_TIME_IN_PAST',
  ThreadIsPinned = 'THREAD_IS_PINNED',
  ThreadNotInModerationState = 'THREAD_NOT_IN_MODERATION_STATE',
  ThreadReplyDisabled = 'THREAD_REPLY_DISABLED',
  TitleNotAllowed = 'TITLE_NOT_ALLOWED',
  TopicToPrivateMessage = 'TOPIC_TO_PRIVATE_MESSAGE',
  Unattachable = 'UNATTACHABLE',
  Unknown = 'UNKNOWN',
  UserIsNotOrganizer = 'USER_IS_NOT_ORGANIZER',
  UserIsNull = 'USER_IS_NULL',
  UserNotInExperiment = 'USER_NOT_IN_EXPERIMENT',
  UpdateEventNotAllowedInCurrentState = 'UPDATE_EVENT_NOT_ALLOWED_IN_CURRENT_STATE',
  UploadInvalidFileName = 'UPLOAD_INVALID_FILENAME',
  UploadNoGroupAccess = 'UPLOAD_NO_GROUP_ACCESS',
  UserNotMapped = 'USER_NOT_MAPPED',
  UserNotPresentInGroup = 'USER_NOT_PRESENT_IN_GROUP',
  VideoUploadMaxDurationExceeded = 'VIDEO_UPLOAD_MAX_DURATION_EXCEEDED',
  MediaFileFormatNotSupported = 'MEDIA_FILE_FORMAT_NOT_SUPPORTED',
}
